import { useState } from 'react'
import { apiClient } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  UserIcon,
} from '@heroicons/react/24/outline'

interface CmuResult {
  nni: string
  covered: boolean
  disposition: string
  plafond?: string
  validUntil?: string
  raw?: string
}

const QUICK_NNI = [
  { nni: '1234567890', label: 'DIOP Moussa', hint: 'Couvert - contributive' },
  { nni: '1234567891', label: 'NDIAYE Fatou', hint: 'Couvert - contributive' },
  { nni: '1234567910', label: 'SY Demba', hint: 'Couvert - gratuite' },
  { nni: '1234567925', label: 'DIOP Samba', hint: 'Non couvert' },
  { nni: '9999999999', label: 'INCONNU Test', hint: 'Non couvert' },
]

export default function CmuCheckPage() {
  const [nni, setNni] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<CmuResult[]>([])
  const [error, setError] = useState<string | null>(null)

  async function checkEligibility(nniValue: string) {
    const trimmed = nniValue.trim()
    if (!trimmed) return

    setLoading(true)
    setError(null)

    try {
      const fhirRequest = {
        resourceType: 'CoverageEligibilityRequest',
        status: 'active',
        purpose: ['benefits'],
        patient: {
          identifier: {
            system: 'http://sante.gouv.sn/nni',
            value: trimmed,
          },
        },
        created: new Date().toISOString().split('T')[0],
        insurer: { display: 'CMU Senegal' },
      }

      const { data } = await apiClient.post('/fhir/CoverageEligibilityRequest', fhirRequest, {
        headers: { 'Content-Type': 'application/fhir+json' },
      })

      const responseText = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
      const parsed = typeof data === 'string' ? JSON.parse(data) : data

      const covered = parsed.outcome === 'complete'
      const disposition = parsed.disposition || (covered ? 'Couvert' : 'Non couvert')

      // Extract plafond and validUntil from disposition text
      let plafond: string | undefined
      let validUntil: string | undefined
      const plafondMatch = disposition.match(/Plafond:\s*([^|]+)/)
      const validMatch = disposition.match(/Valide jusqu'au:\s*(\S+)/)
      if (plafondMatch) plafond = plafondMatch[1].trim()
      if (validMatch) validUntil = validMatch[1].trim()

      const result: CmuResult = {
        nni: trimmed,
        covered,
        disposition,
        plafond,
        validUntil,
        raw: responseText,
      }

      setResults((prev) => [result, ...prev])
      setNni('')
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la verification')
    } finally {
      setLoading(false)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    checkEligibility(nni)
  }

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-neutral-900">Verification CMU</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Verifier l'eligibilite d'un patient a la Couverture Maladie Universelle via X-Road
        </p>
      </div>

      {/* Search form */}
      <div className="bg-white rounded-lg border border-neutral-200 p-5 mb-6">
        <form onSubmit={handleSubmit} className="flex gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={nni}
              onChange={(e) => setNni(e.target.value)}
              placeholder="Saisir le NNI du patient (ex: 1234567890)"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !nni.trim()}
            className={cn(
              'px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors',
              loading || !nni.trim()
                ? 'bg-neutral-300 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700'
            )}
          >
            {loading ? (
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
            ) : (
              'Verifier'
            )}
          </button>
        </form>

        {/* Quick NNI buttons */}
        <div className="mt-4">
          <p className="text-xs text-neutral-400 mb-2">Acces rapide (demo) :</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_NNI.map((q) => (
              <button
                key={q.nni}
                type="button"
                onClick={() => checkEligibility(q.nni)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors disabled:opacity-50"
                title={q.hint}
              >
                <UserIcon className="w-3.5 h-3.5" />
                {q.label}
                <span className="text-neutral-400">({q.nni})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-700">
              Resultats ({results.length})
            </h2>
            <button
              onClick={() => setResults([])}
              className="text-xs text-neutral-400 hover:text-neutral-600"
            >
              Effacer tout
            </button>
          </div>

          {results.map((r, i) => (
            <ResultCard key={`${r.nni}-${i}`} result={r} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {results.length === 0 && !error && (
        <div className="text-center py-16 text-neutral-400">
          <ShieldCheckIcon className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
          <p className="text-sm">Saisissez un NNI pour verifier l'eligibilite CMU</p>
          <p className="text-xs mt-1">
            La requete transite par X-Road : SANTE (SS3) → CMU (SS2) → Registre
          </p>
        </div>
      )}
    </div>
  )
}

function ResultCard({ result }: { result: CmuResult }) {
  const [showRaw, setShowRaw] = useState(false)

  return (
    <div
      className={cn(
        'rounded-lg border p-5 transition-all',
        result.covered
          ? 'bg-emerald-50 border-emerald-200'
          : 'bg-red-50 border-red-200'
      )}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
            result.covered ? 'bg-emerald-100' : 'bg-red-100'
          )}
        >
          {result.covered ? (
            <ShieldCheckIcon className="w-6 h-6 text-emerald-600" />
          ) : (
            <ShieldExclamationIcon className="w-6 h-6 text-red-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-bold text-neutral-900">NNI {result.nni}</span>
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                result.covered
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-red-100 text-red-800'
              )}
            >
              {result.covered ? 'COUVERT' : 'NON COUVERT'}
            </span>
          </div>

          <p className="text-sm text-neutral-700">{result.disposition}</p>

          {/* Details for covered */}
          {result.covered && (result.plafond || result.validUntil) && (
            <div className="flex gap-6 mt-3">
              {result.plafond && (
                <div>
                  <p className="text-xs text-neutral-500">Plafond</p>
                  <p className="text-sm font-semibold text-neutral-900">{result.plafond}</p>
                </div>
              )}
              {result.validUntil && (
                <div>
                  <p className="text-xs text-neutral-500">Valide jusqu'au</p>
                  <p className="text-sm font-semibold text-neutral-900">{result.validUntil}</p>
                </div>
              )}
            </div>
          )}

          {/* Raw FHIR toggle */}
          <button
            onClick={() => setShowRaw(!showRaw)}
            className="mt-3 text-xs text-neutral-400 hover:text-neutral-600 underline"
          >
            {showRaw ? 'Masquer' : 'Voir'} la reponse FHIR
          </button>

          {showRaw && result.raw && (
            <pre className="mt-2 p-3 rounded bg-white/80 border border-neutral-200 text-xs text-neutral-600 overflow-x-auto max-h-64">
              {result.raw}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}
